import { type ReactNode, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type LayoutChangeEvent, type ScrollView as RNScrollView, useWindowDimensions } from "react-native";
import { Dialog, isWeb, ScrollView, styled, type TamaguiElement, YStack } from "tamagui";
import { shadow } from "../../themes/token-shadow";
import { BackgroundRadialGrantedGradient } from "../BackgroundRadialGrantedGradient";
import { presenceTransition } from "../Motion/motion";
import { NavHeader } from "../NavHeader";
import { ModalFooter, modalPaddingBySize } from "./ModalParts";
import { ModalScrollProvider } from "./ModalScrollContext";
import type { ModalFooterProps, ModalHeaderProps, ModalSize } from "./types";
export const modalMaxWidthBySize = {
  small: 440,
  medium: 666,
  large: 892
} as const;
const DEFAULT_MAX_HEIGHT = 800;
const scrollContentContainerStyle = {
  flexGrow: 1,
  minHeight: "100%"
} as const;
const desktopHeaderPaddingBySize = {
  small: {
    paddingVertical: "$sp1",
    paddingHorizontal: "$sp0"
  },
  medium: {
    paddingVertical: "$sp1",
    paddingHorizontal: "$sp1"
  },
  large: {
    paddingVertical: "$sp2",
    paddingHorizontal: "$sp2"
  }
} as const;
const StyledDialogContent = styled(Dialog.Content, {
  name: "DialogContent",
  backgroundColor: "$surface1",
  overflow: "hidden",
  padding: 0,
  transition: {
    ...presenceTransition,
    enter: "overlay"
  },
  enterStyle: {
    opacity: 0,
    y: 8,
    scale: 0.96
  },
  opacity: 1,
  exitStyle: {
    opacity: 0,
    y: 8,
    scale: 0.96
  },
  variants: {
    modalSize: {
      small: {
        width: "90%",
        maxWidth: modalMaxWidthBySize.small,
        borderRadius: "$br3"
      },
      medium: {
        width: "90%",
        maxWidth: modalMaxWidthBySize.medium,
        borderRadius: "$br3"
      },
      large: {
        width: "90%",
        maxWidth: modalMaxWidthBySize.large,
        borderRadius: "$br3"
      }
    },
    fullScreen: {
      true: {
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        borderRadius: "$br0"
      }
    },
    shadow
  } as const,
  defaultVariants: {
    modalSize: "large",
    shadow: "heavy"
  }
});

/**
 * Web-only: measures content height via onLayout, caps at maxHeight, and
 * animates changes with a CSS transition. A ScrollView inside handles overflow.
 * Put padding on children (not around this component) so the scrollbar stays
 * flush with the modal edge.
 */
function AnimatedHeight({
  children,
  maxHeight,
  scrollViewRef
}: {
  children: ReactNode;
  maxHeight?: number;
  scrollViewRef: RefObject<RNScrollView | null>;
}) {
  const [height, setHeight] = useState<number | null>(null);
  const lastHeightRef = useRef<number | null>(null);
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const measured = event.nativeEvent.layout.height;
    const capped = maxHeight !== undefined ? Math.min(measured, maxHeight) : measured;
    if (lastHeightRef.current !== capped) {
      lastHeightRef.current = capped;
      setHeight(capped);
    }
  }, [maxHeight]);
  return <YStack height={height ?? undefined} maxHeight="100%" overflow="hidden" transition="expand" animateOnly={["height"]} data-sentry-element="YStack" data-sentry-component="AnimatedHeight" data-sentry-source-file="DialogModal.tsx">
      <ScrollView ref={scrollViewRef} flex={1} data-sentry-element="ScrollView" data-sentry-source-file="DialogModal.tsx">
        <YStack onLayout={onLayout} data-sentry-element="YStack" data-sentry-source-file="DialogModal.tsx">{children}</YStack>
      </ScrollView>
    </YStack>;
}
export type DialogModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Render the design-system gradient as a full-bleed background behind the surface. */
  gradientBackground?: boolean;
  surface?: "surface1" | "surface2";
  headerProps: ModalHeaderProps;
  footerProps?: ModalFooterProps;
  size?: ModalSize;
  scrollable?: boolean;
  /** Animate height changes (web only) */
  animateHeight?: boolean;
  /** Max overall dialog height (web only). Actual max is the smaller of this and windowHeight - 200. */
  maxHeight?: number;
  /** Make the modal always fill its max height instead of shrinking to content */
  fillHeight?: boolean;
};

/**
 * Dialog Modal component for web and native tablets/desktops.
 * Renders a centered dialog with header and footer derived from props.
 *
 * @example
 * <DialogModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   headerProps={{ title: "Settings" }}
 *   footerProps={{ primaryButtonProps: { children: "Save" } }}
 * >
 *   <Text>Content</Text>
 * </DialogModal>
 */
export const DialogModal = ({
  open,
  onOpenChange,
  children,
  gradientBackground = false,
  surface = "surface1",
  headerProps,
  footerProps,
  size = "medium",
  scrollable = true,
  animateHeight = true,
  maxHeight: maxHeightProp = DEFAULT_MAX_HEIGHT,
  fillHeight = false
}: DialogModalProps) => {
  const {
    height: windowHeight,
    width: windowWidth
  } = useWindowDimensions();
  const isFullScreen = windowWidth < modalMaxWidthBySize[size] / 0.9;
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);
  const focusTargetRef = useRef<TamaguiElement>(null);
  const scrollViewRef = useRef<RNScrollView>(null);
  const dialogRef = useRef<TamaguiElement>(null);

  // Without `open` on <dialog>, screen readers skip its contents. Tamagui's
  // `node.show?.()` no-ops under jsdom and races SSR — set it ourselves.
  useEffect(() => {
    if (!isWeb) {
      return;
    }
    const node = dialogRef.current;
    if (!(node instanceof HTMLDialogElement)) {
      return;
    }
    if (open) {
      node.setAttribute("open", "");
    } else {
      node.removeAttribute("open");
    }
  }, [open]);

  // Prevent Dialog from auto-focusing header elements on open
  const handleOpenAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    if (isWeb && focusTargetRef.current instanceof HTMLElement) {
      focusTargetRef.current.focus();
    }
  }, []);
  const computedMaxHeight = useMemo(() => {
    if (isFullScreen) {
      return undefined;
    }
    return Math.min(windowHeight - 200, maxHeightProp);
  }, [windowHeight, isFullScreen, maxHeightProp]);
  const renderContent = () => {
    const horizontalPadding = modalPaddingBySize[size].horizontal;
    const paddingBottom = footerProps ? undefined : modalPaddingBySize[size].footerVertical;

    // Padding inside AnimatedHeight keeps the scrollbar flush with the modal edge.
    if (animateHeight && !isFullScreen && !fillHeight) {
      return <YStack flex={1} minHeight={0}>
          <AnimatedHeight scrollViewRef={scrollViewRef}>
            <YStack paddingHorizontal={horizontalPadding} paddingBottom={paddingBottom}>
              {children}
            </YStack>
          </AnimatedHeight>
        </YStack>;
    }
    const paddedContent = <YStack flex={1} paddingHorizontal={horizontalPadding} paddingBottom={paddingBottom}>
        {children}
      </YStack>;

    // Full-screen modals always scroll — no other way to reach overflow content.
    if (scrollable || isFullScreen) {
      return <ScrollView ref={scrollViewRef} flex={1} contentContainerStyle={scrollContentContainerStyle}>
          {paddedContent}
        </ScrollView>;
    }
    return paddedContent;
  };
  return <Dialog open={open} onOpenChange={onOpenChange} modal data-sentry-element="Dialog" data-sentry-component="DialogModal" data-sentry-source-file="DialogModal.tsx">
      <Dialog.Portal ref={dialogRef} zIndex="$modal" data-sentry-element="Dialog.Portal" data-sentry-source-file="DialogModal.tsx">
        <Dialog.Overlay key="overlay" backgroundColor="$utilityOverlayLight" opacity={1} transition="fade" enterStyle={{
        opacity: 0
      }} exitStyle={{
        opacity: 0
      }} style={isWeb ? {
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)"
      } : undefined} data-sentry-element="Dialog.Overlay" data-sentry-source-file="DialogModal.tsx" />
        <StyledDialogContent key="content" backgroundColor={`$${surface}`} modalSize={isFullScreen ? undefined : size} fullScreen={isFullScreen || undefined} height={!isFullScreen && fillHeight ? computedMaxHeight : undefined} maxHeight={!isFullScreen ? computedMaxHeight : undefined} style={isFullScreen && isWeb ? {
        height: "100dvh",
        minHeight: "100dvh",
        maxHeight: "100dvh"
      } : undefined} onOpenAutoFocus={handleOpenAutoFocus} data-sentry-element="StyledDialogContent" data-sentry-source-file="DialogModal.tsx">
          {gradientBackground ? <BackgroundRadialGrantedGradient /> : null}
          <YStack ref={focusTargetRef} width="100%" flex={1} zIndex={1}
        // Make programmatically focusable on web for dialog focus management.
        // tabIndex={-1} allows .focus() but keeps element out of the Tab order.
        tabIndex={isWeb ? -1 : undefined} outlineStyle={isWeb ? "none" : undefined} data-sentry-element="YStack" data-sentry-source-file="DialogModal.tsx">
            {/* Dialog modal header padding: fullScreen uses small; desktop uses size-based padding */}
            <YStack paddingVertical={isFullScreen ? desktopHeaderPaddingBySize.small.paddingVertical : desktopHeaderPaddingBySize[size].paddingVertical} paddingHorizontal={isFullScreen ? desktopHeaderPaddingBySize.small.paddingHorizontal : desktopHeaderPaddingBySize[size].paddingHorizontal} data-sentry-element="YStack" data-sentry-source-file="DialogModal.tsx">
              <NavHeader {...headerProps} onClosePress={headerProps.onClosePress ?? handleClose} isDialogHeader data-sentry-element="NavHeader" data-sentry-source-file="DialogModal.tsx" />
            </YStack>
            <ModalScrollProvider scrollViewRef={scrollViewRef} data-sentry-element="ModalScrollProvider" data-sentry-source-file="DialogModal.tsx">
              {renderContent()}
            </ModalScrollProvider>
            {footerProps ? <ModalFooter {...footerProps} size={size} isNativeSheet={false} /> : null}
          </YStack>
        </StyledDialogContent>
      </Dialog.Portal>
    </Dialog>;
};